import { loadMod, getAvailableModNames } from 'config/ModuleMap';
import resolveModExport from './resolveModExport';

// 正在异步加载中的 Store Promise，避免同一个 Store 被重复触发加载。
const pendingStores = {};
// 订阅运行时 Store 变化的监听器集合，给自定义 Provider / inject 刷新用。
const listeners = new Set();
// 所有允许懒加载的 Store 名称白名单，来源于自动生成的 ModuleMap。
const availableModNames = new Set(getAvailableModNames());
// 当前运行时真正持有 Store 实例的容器。
let storeContainer = null;

// Store 容器发生变化后，通知所有订阅方刷新。
function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('[runtimeStoreRegistry] listener 执行失败:', error);
    }
  });
}

// 读取全局 Store 容器。
// 这里优先复用 window.Stores，保证运行时动态注册和原有全局 Stores 指向同一份对象。
export function getStoreContainer() {
  if (window.Stores) {
    storeContainer = window.Stores;
  }

  // 首次访问且还没初始化时，先兜底成空对象，避免调用方判空。
  if (!storeContainer) {
    storeContainer = {};
  }

  // 确保 window.Stores 始终可用，便于旧代码和新运行时注册逻辑共享。
  if (!window.Stores) {
    window.Stores = storeContainer;
  }

  return storeContainer;
}

// 初始化运行时 Store 容器。
// 应用启动时会把 首屏加载必须得mod 挂进来；如果运行时已经提前注册过部分 Store，则反向合并回初始容器。
export function initializeStoreContainer(initialStores) {
  if (!storeContainer) {
    storeContainer = initialStores || {};
  } else if (initialStores && storeContainer !== initialStores) {
    // 把已经动态注册过的 Store 合并回初始化容器，避免引用断裂。
    Object.assign(initialStores, storeContainer);
    storeContainer = initialStores;
  }

  window.Stores = storeContainer;
  // 初始化完成后也广播一次，让 Provider / inject 拿到最新容器。
  notify();
  return storeContainer;
}

// 按名称读取某个运行时 Store，不存在时返回 null，减少调用方判空分支。
export function getRuntimeStore(storeName) {
  return getStoreContainer()[storeName] || null;
}

// 向运行时容器注册一个 Store 实例，并立即通知订阅方刷新。
export function registerRuntimeStore(storeName, store) {
  const container = getStoreContainer();
  container[storeName] = store;
  notify();
  return store;
}

// 判断某个 Store 是否已经真实存在于运行时容器中。
export function hasRuntimeStore(storeName) {
  return !!getRuntimeStore(storeName);
}

// 订阅运行时 Store 容器变化。
// 返回取消订阅函数，供 React effect / class 生命周期清理使用。
export function subscribeStoreRegistry(listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

// 暴露当前 Store 容器快照，通常给自定义 Provider 拼接 context 用。
export function getStoreSnapshot() {
  return getStoreContainer();
}

// 判断某个 Store 是否被声明为“可懒加载”。
// 这里只看 ModuleMap 白名单，不代表它已经被真正加载。
export function isLazyStore(storeName) {
  return availableModNames.has(storeName);
}

// 确保单个 Store 已经准备好。
// 返回值可能是：
// 1. 已存在的 Store 实例
// 2. 懒加载完成后的 Store 实例
// 3. 不在白名单内时返回 null
export async function ensureRuntimeStore(storeName) {
  // 已经加载过的 Store 直接复用，避免重复创建实例。
  const cachedStore = getRuntimeStore(storeName);
  if (cachedStore) {
    return cachedStore;
  }

  // 既不在运行时容器里，也不在懒加载白名单里，调用方自己决定如何报错。
  if (!isLazyStore(storeName)) {
    return null;
  }

  // 同一个 Store 在并发场景下只触发一次 loadMod，其他调用共享同一个 Promise。
  if (!pendingStores[storeName]) {
    pendingStores[storeName] = loadMod(storeName)
      // 兼容 default export、class export、对象 export 等不同模块形态。
      .then((modExport) => resolveModExport(modExport))
      // 模块解析完成后，注册到全局运行时容器。
      .then((store) => registerRuntimeStore(storeName, store))
      .finally(() => {
        // 无论成功失败，都清理 pending 标记，避免后续一直卡死在加载中状态。
        delete pendingStores[storeName];
      });
  }

  return pendingStores[storeName];
}

// 批量确保多个 Store 都已准备好。
// 只会对“尚未加载且允许懒加载”的 Store 发起请求。
export async function ensureRuntimeStores(storeNames) {
  const needLoad = storeNames.filter(
    (storeName) => !hasRuntimeStore(storeName) && isLazyStore(storeName)
  );

  // 没有缺失 Store 时返回空数组，调用方可以直接跳过后续逻辑。
  if (!needLoad.length) {
    return [];
  }

  // 并行加载多个 Store，加快首屏后预加载和按页注入速度。
  return Promise.all(needLoad.map((storeName) => ensureRuntimeStore(storeName)));
}

// 返回当前已经注册到运行时容器中的 Store 名称列表，便于调试或埋点观察。
export function getLoadedStoreNames() {
  return Object.keys(getStoreContainer());
}

// 默认导出一组 registry 能力，方便少量场景按对象方式消费。
export default {
  ensureRuntimeStore,
  ensureRuntimeStores,
  getLoadedStoreNames,
  getRuntimeStore,
  getStoreContainer,
  getStoreSnapshot,
  hasRuntimeStore,
  isLazyStore,
  registerRuntimeStore,
  subscribeStoreRegistry
};



// WEBPACK FOOTER //
// ./src/utils/storeRuntime/runtimeStoreRegistry.js